import React from 'react';
import { format } from 'date-fns';
import { X, Calendar, CreditCard, Zap, Repeat, AlertCircle, DollarSign, Wallet, Receipt, Scale } from 'lucide-react';
import { CalendarEvent, BucketCategory } from '../types';
import { normalizeDate, formatDateForInput } from '../utils/dateUtils';
import { addEvent } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { collection, addDoc, Timestamp, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Define available bucket categories
const BUCKET_CATEGORIES = [
  { value: 'groceries', label: 'Groceries' },
  { value: 'dining', label: 'Dining Out' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'personal', label: 'Personal Care' },
  { value: 'gifts', label: 'Gifts' },
  { value: 'pet', label: 'Pet Expenses' },
  { value: 'other', label: 'Other' }
] as const;

// Define regular bill categories
const BILL_CATEGORIES = [
  { value: 'food', label: 'Food & Dining' },
  { value: 'groceries', label: 'Groceries' },
  { value: 'dining', label: 'Restaurants' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'housing', label: 'Housing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'medical', label: 'Medical Expenses' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'education', label: 'Education' },
  { value: 'travel', label: 'Travel' },
  { value: 'personal', label: 'Personal' },
  { value: 'gifts', label: 'Gifts' },
  { value: 'savings', label: 'Savings' },
  { value: 'debt', label: 'Debt' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'pet', label: 'Pet & Vet Expenses' },
  { value: 'other', label: 'Other' }
];

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date | null;
  eventType: 'payday' | 'bill' | 'adjustment' | 'bucket' | null;
  onEventTypeChange?: (type: 'payday' | 'bill' | 'adjustment' | 'bucket') => void;
  event?: CalendarEvent | null;
  onEventUpdate?: (event: CalendarEvent) => void;
  events?: CalendarEvent[];
}

function EventModal({
  isOpen,
  onClose,
  date,
  eventType,
  onEventTypeChange,
  event,
  onEventUpdate,
  events
}: EventModalProps) {
  const { user } = useAuth();
  const [title, setTitle] = React.useState(event?.title || '');
  const [amount, setAmount] = React.useState(event?.amount?.toString() || '');
  const [selectedDate, setSelectedDate] = React.useState(
    event?.date ? formatDateForInput(event.date) : 
    date ? formatDateForInput(date) : 
    formatDateForInput(new Date())
  );
  const [isRecurring, setIsRecurring] = React.useState(event?.isRecurring || false);
  const [frequency, setFrequency] = React.useState(event?.frequency || 'monthly');
  const [savingsPercentage, setSavingsPercentage] = React.useState(event?.savingsPercentage?.toString() || '');
  const [isSubscription, setIsSubscription] = React.useState(event?.isSubscription || false);
  const [isAutopay, setIsAutopay] = React.useState(event?.isAutopay || false);
  const [isScheduled, setIsScheduled] = React.useState(event?.isScheduled || false);
  const [category, setCategory] = React.useState(event?.category || '');
  const [otherCategoryName, setOtherCategoryName] = React.useState('');
  const [showCategoryError, setShowCategoryError] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load bucket data when editing
  React.useEffect(() => {
    const loadBucketData = async () => {
      if (event?.type === 'bucket' && event?.bucketId) {
        try {
          const bucketDoc = await getDoc(doc(db, 'budgetBuckets', event.bucketId));
          if (bucketDoc.exists()) {
            const bucketData = bucketDoc.data();
            setCategory(bucketData.category);
            setAmount(event.amount.toString());
            if (bucketData.category === 'other') {
              setOtherCategoryName(bucketData.name);
            }
          }
        } catch (error) {
          console.error('Error loading bucket data:', error);
          setError('Failed to load bucket data');
        }
      }
    };

    loadBucketData();
  }, [event]);

  // Reset form when event type changes
  React.useEffect(() => {
    if (eventType === 'bucket' && !event) {
      setTitle('');
      setAmount('');
      setCategory('');
      setOtherCategoryName('');
      setIsRecurring(false);
      setIsSubscription(false);
      setIsAutopay(false);
      setIsScheduled(false);
    }
  }, [eventType, event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !eventType || !user) return;

    if ((eventType === 'bill' || eventType === 'bucket') && !category) {
      setShowCategoryError(true);
      return;
    }

    if (category === 'other' && !otherCategoryName.trim()) {
      setError('Please provide a name for the Other category');
      return;
    }

    try {
      const eventDate = normalizeDate(selectedDate);
      const eventAmount = parseFloat(amount);

      if (eventType === 'bucket') {
        const selectedCategory = BUCKET_CATEGORIES.find(cat => cat.value === category);
        if (!selectedCategory) {
          setError('Invalid category selected');
          return;
        }

        let bucketId = event?.bucketId;
        
        if (!bucketId) {
          // Check for existing bucket with same category
          const bucketsRef = collection(db, 'budgetBuckets');
          const q = query(
            bucketsRef,
            where('userId', '==', user.uid),
            where('category', '==', category)
          );
          const existingBuckets = await getDocs(q);

          if (!existingBuckets.empty) {
            // Use existing bucket
            bucketId = existingBuckets.docs[0].id;
          } else {
            // Create new bucket with zero initial amount
            const bucketRef = await addDoc(collection(db, 'budgetBuckets'), {
              userId: user.uid,
              name: category === 'other' ? otherCategoryName : selectedCategory.label,
              category,
              initialAmount: 0,
              remainingAmount: 0,
              createdAt: Timestamp.now(),
              transactions: []
            });
            bucketId = bucketRef.id;
          }
        }

        // Create or update calendar event for the bucket
        const newEvent: CalendarEvent = {
          id: event?.id || crypto.randomUUID(),
          title: `${category === 'other' ? otherCategoryName : selectedCategory.label} Budget`,
          amount: eventAmount,
          date: eventDate,
          type: 'bucket',
          bucketId,
          category: category as BucketCategory,
          isPaid: false // Always start as unpaid
        };

        if (event) {
          await onEventUpdate?.(newEvent);
        } else {
          await addEvent(newEvent);
        }
        onClose();
      } else {
        // Handle other event types
        const newEvent: CalendarEvent = {
          id: event?.id || crypto.randomUUID(),
          title: category === 'other' ? otherCategoryName : title,
          amount: eventAmount,
          date: eventDate,
          type: eventType,
          isRecurring,
          frequency: isRecurring ? frequency : undefined,
          savingsPercentage: eventType === 'payday' && savingsPercentage ? parseFloat(savingsPercentage) : undefined,
          isSubscription,
          isAutopay,
          isScheduled,
          category: eventType === 'bill' ? category : undefined,
          isPaid: event?.isPaid || false,
          paidDate: event?.paidDate
        };

        if (event) {
          await onEventUpdate?.(newEvent);
        } else {
          await addEvent(newEvent);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error saving event:', error);
      setError('Failed to save event. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {event ? 'Edit' : 'Add'} {eventType?.charAt(0).toUpperCase() + eventType?.slice(1)}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Event Type Selection */}
        {onEventTypeChange && !event && (
          <div className="p-6 border-b">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => onEventTypeChange('payday')}
                className={`flex items-center justify-center p-4 rounded-lg border ${
                  eventType === 'payday' ? 'bg-green-50 border-green-500' : 'hover:bg-gray-50'
                }`}
              >
                <DollarSign className={`w-5 h-5 mr-2 ${
                  eventType === 'payday' ? 'text-green-500' : 'text-gray-400'
                }`} />
                <span>Payday</span>
              </button>
              <button
                onClick={() => onEventTypeChange('bill')}
                className={`flex items-center justify-center p-4 rounded-lg border ${
                  eventType === 'bill' ? 'bg-red-50 border-red-500' : 'hover:bg-gray-50'
                }`}
              >
                <Receipt className={`w-5 h-5 mr-2 ${
                  eventType === 'bill' ? 'text-red-500' : 'text-gray-400'
                }`} />
                <span>Bill</span>
              </button>
              <button
                onClick={() => onEventTypeChange('bucket')}
                className={`flex items-center justify-center p-4 rounded-lg border ${
                  eventType === 'bucket' ? 'bg-purple-50 border-purple-500' : 'hover:bg-gray-50'
                }`}
              >
                <Wallet className={`w-5 h-5 mr-2 ${
                  eventType === 'bucket' ? 'text-purple-500' : 'text-gray-400'
                }`} />
                <span>Budget Bucket</span>
              </button>
              <button
                onClick={() => onEventTypeChange('adjustment')}
                className={`flex items-center justify-center p-4 rounded-lg border ${
                  eventType === 'adjustment' ? 'bg-indigo-50 border-indigo-500' : 'hover:bg-gray-50'
                }`}
              >
                <Scale className={`w-5 h-5 mr-2 ${
                  eventType === 'adjustment' ? 'text-indigo-500' : 'text-gray-400'
                }`} />
                <span>Adjustment</span>
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center text-red-700">
              <AlertCircle className="w-5 h-5 mr-2" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {eventType !== 'bucket' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {eventType === 'bucket' ? 'Amount' : 'Amount'}
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="block w-full pl-7 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          {(eventType === 'bill' || eventType === 'bucket') && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setShowCategoryError(false);
                  if (e.target.value !== 'other') {
                    setOtherCategoryName('');
                  }
                }}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                  showCategoryError ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select a category</option>
                {(eventType === 'bucket' ? BUCKET_CATEGORIES : BILL_CATEGORIES).map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              {showCategoryError && (
                <p className="mt-1 text-sm text-red-600">Please select a category</p>
              )}

              {category === 'other' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Category Name</label>
                  <input
                    type="text"
                    value={otherCategoryName}
                    onChange={(e) => setOtherCategoryName(e.target.value)}
                    placeholder="Enter category name"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
              )}
            </div>
          )}

          {eventType === 'bill' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isSubscription"
                  checked={isSubscription}
                  onChange={(e) => setIsSubscription(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isSubscription" className="ml-2 flex items-center text-sm text-gray-700">
                  <CreditCard className="w-4 h-4 mr-1" />
                  Subscription
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isAutopay"
                  checked={isAutopay}
                  onChange={(e) => setIsAutopay(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isAutopay" className="ml-2 flex items-center text-sm text-gray-700">
                  <Zap className="w-4 h-4 mr-1" />
                  Auto-pay
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isScheduled"
                  checked={isScheduled}
                  onChange={(e) => setIsScheduled(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isScheduled" className="ml-2 flex items-center text-sm text-gray-700">
                  <Calendar className="w-4 h-4 mr-1" />
                  Scheduled
                </label>
              </div>
            </div>
          )}

          {eventType === 'payday' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Automatic Savings (%) - Optional
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={savingsPercentage}
                onChange={(e) => setSavingsPercentage(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          )}

          {eventType !== 'bucket' && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isRecurring"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="isRecurring" className="ml-2 flex items-center text-sm text-gray-700">
                <Repeat className="w-4 h-4 mr-1" />
                Recurring
              </label>
            </div>
          )}

          {isRecurring && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as 'weekly' | 'biweekly' | 'monthly' | 'yearly')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              {event ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventModal;